import React, { useEffect, useMemo, useState } from 'react';
import {
  BoxProps,
  Button,
  Grid,
  HStack,
  IconButton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Slot as SlotComponent } from './Slot';
import { DragLayer } from '../dnd/DragLayer';
import { useDrop } from 'react-dnd';
import { ItemTypes } from '../dnd/types';
import { Content } from '../shared/types';
import { ContentComponent } from '../shared/ContentComponent';
import ContentBox from '../dnd/ContentDrag';
import { shuffleArray } from '../../../../../../helpers/shuffle';
import { dirFromDndPos, flexDirFromDndPos } from '../shared/utils';
import { RefreshIcon } from '../../../../../../components/icons';
import { useIsMobile } from '../../../../../../hooks/isMobile';

export type Slot = {
  key: string;
  index: number;
  answer: string;
  caseSensitive?: string;
};

type Association = {
  left: Content;
  right: Content;
};

interface AssociationsProps extends BoxProps {
  associations: Association[];
  debug?: boolean;
  showCorrect?: boolean;
  dndPosition: 'left' | 'top' | 'right' | 'bottom' | string;
  submit?: (correct: number, total: number, state: any) => void;
  reset?: () => void;
  initialState?: State;
  isViewOnly?: boolean;
}

type State = {
  answers: Record<
    string,
    { value: string; correct: boolean; type: 'text' | 'image' | 'audio' }
  >;
  slot: Record<string, string>;
};

const Associations: React.FC<AssociationsProps> = ({
  associations,
  debug,
  showCorrect,
  dndPosition = 'right',
  submit,
  reset,
  initialState,
  isViewOnly,
  ...rest
}) => {
  const [state, setState] = useState<State>({ answers: {}, slot: {} });
  const [showCorrectAnswers, setShowCorrectAnswers] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [shownCorrect, setShownCorrect] = useState<number>(0);
  // const indexMap = useMemo(() => {
  //   const map: Map<number, Slot> = new Map();

  //   associations.forEach((s, index) => {
  //     map.set(index, s);
  //   });

  //   return map;
  // }, [associations]);
  const isMobile = useIsMobile();

  const [cAssociations, shuffledAssocs, hasImg] = useMemo(() => {
    let hasImg = false;
    const assocs = associations.map((a, index) => {
      if (a.left.type === 'image' || a.right.type === 'image') {
        hasImg = true;
      }
      return {
        ...a,
        slot: {
          index,
          type: a.right.type,
          answer: a.right.src,
          key: `a${index + 1}`,
        },
      };
    });

    const shuffledAssocs = [...assocs];

    shuffleArray(shuffledAssocs);

    return [assocs, shuffledAssocs, hasImg];
  }, []);

  const [{ hovering }, drop] = useDrop({
    accept: ItemTypes.ContentBox,
    drop: (item: { id: string }, monitor) => {
      if (monitor.isOver({ shallow: true })) {
        let newState = { ...state };
        const answerKey = newState.slot[item.id]!;
        delete newState.answers[answerKey];
        delete newState.slot[item.id];
        setState(newState);
      }
    },
    collect: (monitor) => ({
      hovering: monitor.isOver(),
    }),
  });

  const updateValueFn = (
    toKey: string,
    newValue: string,
    fromKey: string,
    type: 'text' | 'image' | 'audio',
  ) => {
    let newState = { ...state };
    let oldAnswerKey = newState.slot[fromKey];
    let oldSlot = Object.keys(newState.slot).find(
      (s) => newState.slot[s] === toKey,
    );
    if (oldAnswerKey) {
      if (newState.answers[toKey] != null) {
        newState.answers[oldAnswerKey] = { ...newState.answers[toKey]! };
      } else {
        delete newState.answers[oldAnswerKey];
      }
    }
    if (oldSlot) {
      if (newState.slot[oldSlot] != null) {
        newState.slot[oldSlot] = oldAnswerKey!;
      }
    }
    newState.slot[fromKey] = toKey;
    newState.answers[toKey]! = {
      value: newValue,
      correct: false,
      type,
    };
    Object.keys(newState.answers).forEach((key) => {
      const assoc = cAssociations.find((s) => s.slot.key === key);
      if (assoc) {
        newState.answers[key]!.correct =
          newState.answers[key]!.value === assoc.slot.answer;
      }
    });
    setState(newState);
  };

  const setInitialState = () => {
    setState({ answers: {}, slot: {} });
  };

  useEffect(() => {
    if (initialState) {
      setState(initialState);
      setShowResults(true);
      let correct = 0;

      Object.keys(initialState.answers).forEach((key) => {
        if (initialState.answers[key]!.correct) {
          correct += 1;
        }
      });

      setShownCorrect(correct);
    } else setInitialState();
  }, [associations, initialState]);

  const _submit = () => {
    setShowResults(true);
    let correct = 0;

    Object.keys(state.answers).forEach((key) => {
      if (state.answers[key]!.correct) {
        correct += 1;
      }
    });

    setShownCorrect(correct);
    if (submit != null) {
      submit(correct, associations.length, state);
    }
  };

  const _reset = () => {
    setInitialState();
    setShowResults(false);
    setShownCorrect(0);
    if (reset != null) reset();
  };

  const ret = (
    <VStack w="full" spacing={0} gap={4}>
      <Stack
        w="full"
        ref={drop}
        alignItems="center"
        gap={4}
        display="flex"
        flexDir={flexDirFromDndPos(dndPosition)}
        {...rest}
      >
        <VStack flexGrow={4} justify="space-evenly" w="full" display='flex' flexDirection='column' alignItems='flex-start'>
          {cAssociations.map((c) => {
            const s2 = Object.keys(state.slot).find(
              (s) => state.slot[s] === c.slot.key,
            );
            return (
              <Grid
                key={c.slot.key}
                w="full"
                gap={2}
                gridTemplateColumns={'1fr auto 1fr'}
                alignContent="center"
                minH={hasImg ? '100px' : undefined}
                padding='10px'
              >
                {ContentComponent({
                  ...c.left,
                  alignSelf: 'center',
                  maxW: c.left.type === 'image' ? '100px' : undefined,
                  maxH: c.left.type === 'image' ? '100px' : undefined,
                  fontSize: isMobile ? '12px' : '16px',
                  minWidth: '80px'
                })}
                <Text as="div" alignSelf="center" color="gray.500"  margin='0 30px'>
                  <b>&lt;=&gt;</b>
                </Text>
                <SlotComponent
                  id={c.slot.key}
                  key={c.slot.key}
                  slot={s2}
                  value={state.answers[c.slot.key]?.value}
                  type={state.answers[c.slot.key]?.type}
                  placeholder={
                    showCorrect || showCorrectAnswers ? c.right : undefined
                  }
                  updateValue={updateValueFn}
                  justifySelf="end"
                  alignSelf="center"
                  minWidth='80px'
                />
              </Grid>
            );
          })}
        </VStack>
        <Stack
          spacing={0}
          gap={2}
          flexGrow={1}
          ml={4}
          background={hovering ? 'rgba(208,208,208,0.45)' : undefined}
          borderRadius="8px"
          direction={dirFromDndPos(dndPosition)}
          w="full"
          minH={dirFromDndPos(dndPosition) === 'row' ? '40px' : undefined}
          minW={dirFromDndPos(dndPosition) === 'column' ? '120px' : undefined}
          align="center"
          alignSelf="stretch"
          p={2}
        >
          <Grid
            templateColumns={`repeat(auto-fill, minmax(${
              dirFromDndPos(dndPosition) === 'row' ? '120px' : '90%'
            }, 1fr))`}
            gap={3}
            w="full"
            justifyItems="center"
          >
            {shuffledAssocs
              .filter(
                (s) =>
                  showCorrectAnswers || state.slot[s.slot.key] === undefined,
              )
              .map((s) => (
                <ContentBox
                  id={s.slot.key}
                  key={s.slot.key}
                  type={s.right.type}
                  src={s.slot.answer}
                  boxShadow="sm"
                  fontSize={isMobile ? '12px' : '16px'}
                  minWidth='80px'
                  maxW={{ base: '100%', md: 'auto' }}
                />
              ))}
          </Grid>
        </Stack>
      </Stack>
      <HStack
        minW={showResults ? '300px' : undefined}
        spacing={0}
        gap={2}
        justify="space-between"
      >
        {showResults && (
          <>
            {showResults && (
              <IconButton
                size="sm"
                colorScheme="stemiBlue"
                variant="ghost"
                aria-label="reset"
                icon={<RefreshIcon boxSize="24px" />}
                onClick={_reset}
              >
                Reset
              </IconButton>
            )}
            <HStack>
              <span>
                {shownCorrect} / {associations.length}
              </span>
            </HStack>
          </>
        )}
        <HStack>
          {!isViewOnly && (
            <Button colorScheme="blue" onClick={_submit}>
              Submit
            </Button>
          )}
        </HStack>
      </HStack>
    </VStack>
  );

  if (debug) {
    return (
      <VStack w="full">
        {ret}
        <DragLayer />
        <Text>
          <pre>{JSON.stringify(state, null, 2)}</pre>
        </Text>
      </VStack>
    );
  }

  return (
    <>
      <DragLayer />
      {ret}
    </>
  );
};

export default Associations;
